import React, { createContext, useCallback, useMemo, useRef, useState } from 'react';
import { useBlocker, useBeforeUnload } from 'react-router-dom';
import MarkModal from './MarkModal';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import { MarkConfirmActions } from './MarkControls';

type TokenState = {
    dirty: boolean;
    saving: boolean;
};

type UnsavedChangesContextValue = {
    anySaving: boolean;
    register: (token: string, dirty: boolean, saving?: boolean) => void;
    unregister: (token: string) => void;
    isDirty: (token: string) => boolean;
    isSaving: (token: string) => boolean;
    // Runs when the app-wide "Discard unsaved changes?" prompt is confirmed,
    // right before the blocked navigation is allowed to proceed. The
    // blocker itself only knows how to continue a navigation — it has no
    // idea a token's owner (e.g. a drip's structural draft) needs its own
    // state reset, and a component that stays mounted across the
    // navigation (a tab switch, not a page leave) would otherwise keep
    // showing the "discarded" edits. Returns an unregister function.
    registerDiscardHandler: (handler: () => void) => () => void;
};

export const UnsavedChangesContext = createContext<UnsavedChangesContextValue>({
    anySaving: false,
    register: () => {},
    unregister: () => {},
    isDirty: () => false,
    isSaving: () => false,
    registerDiscardHandler: () => () => {}
});

type Props = {
    children: React.ReactNode;
};

const UnsavedChangesProvider: React.FC<Props> = ({ children }) => {
    const [tokens, setTokens] = useState<Map<string, TokenState>>(new Map());

    const register = useCallback((token: string, dirty: boolean, saving = false) => {
        setTokens((prev) => {
            const next = new Map(prev);
            next.set(token, { dirty, saving });
            return next;
        });
    }, []);

    const unregister = useCallback((token: string) => {
        setTokens((prev) => {
            const next = new Map(prev);
            next.delete(token);
            return next;
        });
    }, []);

    const hasUnsavedChanges = useMemo(
        () => Array.from(tokens.values()).some((t) => t.dirty),
        [tokens]
    );

    const anySaving = useMemo(() => Array.from(tokens.values()).some((t) => t.saving), [tokens]);

    const isDirty = useCallback((token: string) => tokens.get(token)?.dirty ?? false, [tokens]);

    const isSaving = useCallback((token: string) => tokens.get(token)?.saving ?? false, [tokens]);

    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        if (!hasUnsavedChanges) return false;
        // Variant navigation keeps the same workspace and all its drafts mounted.
        const current = new URLSearchParams(currentLocation.search);
        const next = new URLSearchParams(nextLocation.search);
        const variantChanged = current.get('variant') !== next.get('variant');
        current.delete('variant');
        next.delete('variant');
        const preservedVariantNavigation =
            /^\/mark\/blasts\/[1-9]\d*\/template\/?$/.test(currentLocation.pathname) &&
            currentLocation.pathname === nextLocation.pathname &&
            currentLocation.hash === nextLocation.hash &&
            current.toString() === next.toString() &&
            variantChanged;
        // Detail tab switches keep the same step draft mounted.
        const currentDrip = new URLSearchParams(currentLocation.search);
        const nextDrip = new URLSearchParams(nextLocation.search);
        currentDrip.delete('tab');
        nextDrip.delete('tab');
        const preservedDripTabNavigation =
            /^\/mark\/drips\/[1-9]\d*\/?$/.test(currentLocation.pathname) &&
            currentLocation.pathname === nextLocation.pathname &&
            currentLocation.hash === nextLocation.hash &&
            currentLocation.search !== nextLocation.search &&
            currentDrip.toString() === nextDrip.toString();
        return !preservedVariantNavigation && !preservedDripTabNavigation;
    });
    const isBlocked = blocker.state === 'blocked';

    const discardHandlersRef = useRef<Set<() => void>>(new Set());
    const registerDiscardHandler = useCallback((handler: () => void) => {
        discardHandlersRef.current.add(handler);
        return () => discardHandlersRef.current.delete(handler);
    }, []);

    useBeforeUnload(
        useCallback(
            (e: BeforeUnloadEvent) => {
                if (hasUnsavedChanges) {
                    e.preventDefault();
                    e.returnValue = '';
                }
            },
            [hasUnsavedChanges]
        )
    );

    const handleDiscard = useCallback(() => {
        if (blocker.state === 'blocked') {
            discardHandlersRef.current.forEach((handler) => handler());
            blocker.proceed();
        }
    }, [blocker]);

    const handleStay = useCallback(() => {
        if (blocker.state === 'blocked') {
            blocker.reset();
        }
    }, [blocker]);

    const contextValue = useMemo(
        () => ({ register, unregister, isDirty, isSaving, registerDiscardHandler, anySaving }),
        [register, unregister, isDirty, isSaving, registerDiscardHandler, anySaving]
    );

    return (
        <UnsavedChangesContext.Provider value={contextValue}>
            {children}
            {isBlocked && (
                <MarkModal
                    aria-label="Discard unsaved changes?"
                    isPending={anySaving}
                    type="warn"
                    isCancelable
                    styles={{
                        width: 'min(64rem, 94vw)'
                    }}
                    onClose={handleStay}
                >
                    <H role="h4">Discard unsaved changes?</H>
                    <P>You have unsaved changes that will be lost if you leave this page.</P>
                    <MarkConfirmActions
                        confirmLabel="Discard"
                        cancelLabel="Stay"
                        onConfirm={handleDiscard}
                        onCancel={handleStay}
                        disabled={anySaving}
                    />
                </MarkModal>
            )}
        </UnsavedChangesContext.Provider>
    );
};

export default UnsavedChangesProvider;
